import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { homeRoute, forgotPasswordRoute, publicPrivacyPolicyRoute } from './../../core/routing/route-names';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { LoginModel } from '../shared/login.model';
import { SpinnerService } from '../spinner/spinner.service';
import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        AppErrorMessagesComponent,
        NgIf,
        RouterLink,
        TranslateModule,
        CardModule,
        InputTextModule,
        PasswordModule,
        ButtonModule,
        ProgressSpinnerModule
    ]
})
export class LoginComponent implements OnInit {
    public loading = false;
    public loginModel: LoginModel = {} as LoginModel;
    public showPassword = false;
    public apiError: string | null = null;  

    public loginForm: UntypedFormGroup;
    public loginUserNameControl: UntypedFormControl;
    public loginPasswordControl: UntypedFormControl;
    public forgotPasswordRoute = forgotPasswordRoute;
    public publicPrivacyPolicyRoute = publicPrivacyPolicyRoute;

    private initFlag;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private toastService: ToastService,
        private spinnerService: SpinnerService
    ) { }

    ngOnInit() {
        this.spinnerService.showSpinner = false;
        this.initFlag = this.route.snapshot.queryParams.init;
        this.initializeForm();

        if (this.authService.isUserLoggedIn() && !this.initFlag) {
            this.router.navigate([homeRoute]);
        }
    }

    
    public togglePassword(): void {
        this.showPassword = !this.showPassword;
    }

    public login(): void {
        
        Object.keys(this.loginForm.controls).forEach(key => {
            this.loginForm.get(key)?.markAsTouched();
        });

        if (this.loginForm.valid) {
            this.loading = true;
            this.apiError = null; 

            this.loginModel.username = this.loginForm.value.userName;
            this.loginModel.password = this.loginForm.value.password;

            this.authService.login(this.loginModel).subscribe({
                next: (result) => {
                    this.loading = false;
                    if (result) {
                        if (this.authService.redirectUrl) {
                            this.router.navigateByUrl(this.authService.redirectUrl);
                        } else {
                            this.router.navigate([homeRoute]);
                        }
                    }
                },
                error: error => {
                    this.loading = false;
                    
                    const errorCode = error?.error?.errorCode;
                    const errorMessage = error?.error?.message;
                    if (errorCode === 'UserNotFound' || errorCode === 'UnauthorizedAccess') {
                        this.apiError = errorMessage || 'Invalid username or password.';
                    } else if (errorCode === 'UserNotActive') {
                        this.apiError = errorMessage || 'Your account is disabled. Please contact your administrator.';
                    } else {
                        this.apiError = errorMessage || 'Invalid username or password.';
                    }
                }
            });
        }
    }

    private initializeForm(): void {
        this.loginUserNameControl = new FormControl(null, [Validators.required]);
        this.loginPasswordControl = new FormControl(null, [Validators.required]);
        this.loginForm = new UntypedFormGroup({
            userName: this.loginUserNameControl,
            password: this.loginPasswordControl
        });
    }
}